"use client";

import { useTransition } from "react";
import { KlaviyoEventPayload } from "../types/types";
import { sendClientEvent, sendAllEvent } from "../lib/klaviyoActions";
import { useAuth } from "~/ui/hooks/useAuth";
import { useKlaviyoContext } from "../KlaviyoProvider";
import { trackEvent } from "../lib/klaviyoClient";

type ClientEvent = Parameters<typeof sendClientEvent>[0]["event"];

export function useKlaviyo() {
  const [isPending, startTransition] = useTransition();
  const auth = useAuth();
  const klaviyoContext = useKlaviyoContext();

  /**
   * Send an event via server action (for events that need server-side processing)
   */
  const sendServerEvent = async <T extends ClientEvent>(payload: KlaviyoEventPayload<T>) => {
    startTransition(async () => {
      await sendClientEvent(payload);
    });
  };

  /**
   * Track a client-side event directly via Klaviyo SDK
   * This is faster and doesn't require server round-trip
   */
  const trackClientEvent = async (eventName: string, properties?: Record<string, any>) => {
    if (!klaviyoContext.isLoaded) {
      console.warn("Klaviyo SDK not loaded yet");
      return;
    }
    
    await trackEvent(eventName, properties);
  };

  const createKlaviyoUser = async () => {
    if (auth.isAuthenticated && auth.userInfos?.email) {
      await sendAllEvent({
        event: "subscribe",
        data: {
          email: auth.userInfos.email,
        },
      });

      return {
        email: auth.userInfos.email,
      };
    }
  };

  const getKlaviyoUser = () => {
    if (auth.isAuthenticated && auth.userInfos?.email) {
      return {
        email: auth.userInfos.email,
      };
    }
  };

  return {
    // Server-side events (use for events defined in klaviyoActions.ts)
    sendServerEvent,
    // Client-side events (use for quick tracking without server round-trip)
    trackClientEvent,
    // User management
    createKlaviyoUser,
    getKlaviyoUser,
    // State
    isPending,
    isLoaded: klaviyoContext.isLoaded,
    isIdentified: klaviyoContext.isIdentified,
  };
}
